import { buildId } from 'util/globalId'
import { isChatChannelType } from 'ducks/channels/channelTypes'
import { doGraphqlRequest } from 'ducks/requests/operations'
import { selectAccountCanSeePinnedSearches } from 'selectors/app'
import { selectCurrentUserGlobalId } from 'ducks/currentUser/selectors/selectCurrentUserGlobalId'
import { camelize } from 'util/strings'
import { FETCH_CONVERSATION_COUNTS_FOR_CHANNEL } from '../actionTypes'
import {
  ticketConversationCountForChannelQuery,
  ticketConversationCountQuery,
  roomConversationCountForChannelQuery,
} from '../queries'
import {
  ticketConversationCountQueryGraphQlResponseSchema,
  ticketConversationCountForChannelQueryQueryGraphQlResponseSchema,
} from '../schema'
import { buildFetchFolderCountsRequestKey } from '../utils/request'

const normalizeChannelAndGlobal = data => {
  if (data.node) {
    return {
      folders: data.node.folders || { nodes: [] },
      agents: data.node.permittedAgents || { nodes: [] },
      tags: data.node.tags || { nodes: [] },
      channels: data.node.channels || { nodes: [] },
      pinnedSearches: data.node.pinnedSearches || { nodes: [] },
    }
  }
  return {
    folders: data.folders || { nodes: [] },
    agents: data.agents || { nodes: [] },
    tags: data.tags || { nodes: [] },
    channels: data.channels || { nodes: [] },
    pinnedSearches: data.pinnedSearches || { nodes: [] },
  }
}

function hackFolderCountsResponse(channelType) {
  return data => {
    if (!data) return null

    if (isChatChannelType(channelType)) {
      const { roomsCounts: { lastUpdatedAt, data: folders } = {} } = data || {}
      return {
        folders: {
          // IMPORTANT: We dont currently have this available for the ticket conversation
          // counts. We'll need to extend the api to also expose this
          lastUpdatedAt,
          nodes: folders,
        },
        agents: { nodes: [] },
        tags: { nodes: [] },
        channels: { nodes: [] },
        pinnedSearches: { nodes: [] },
      }
    }
    const normalized = normalizeChannelAndGlobal(data)
    normalized.folders.lastUpdatedAt =
      normalized.folders.lastUpdatedAt || Date.now()
    return normalized
  }
}

/*
  This method will be used to retrieve the conversation counts for a particular channel.
  Currently there is a massive disparity in the api implementation between tickets and
  rooms. This method will hide those differences and return a standadised interface for
  the folder component.
 */
export const doFetchFolderCounts = ({ channelId, channelType }) => (
  dispatch,
  getState
) => {
  const state = getState()
  const isChat = isChatChannelType(channelType)
  const currentUserId = selectCurrentUserGlobalId(state)
  let query = roomConversationCountForChannelQuery()
  let normalizationSchema = null
  const canUsePinnedSearches = selectAccountCanSeePinnedSearches(state)
  if (!isChat) {
    if (channelId) {
      query = ticketConversationCountForChannelQuery({ canUsePinnedSearches })
      normalizationSchema = ticketConversationCountForChannelQueryQueryGraphQlResponseSchema
    } else {
      query = ticketConversationCountQuery({ canUsePinnedSearches })
      normalizationSchema = ticketConversationCountQueryGraphQlResponseSchema
    }
  }
  return dispatch(
    doGraphqlRequest(
      FETCH_CONVERSATION_COUNTS_FOR_CHANNEL,
      query,
      {
        channelId: buildId('Channel', channelId),
        channelType: channelType?.toUpperCase(),
        pinnedSearchFilter: {
          agentId: currentUserId,
        },
      },
      {
        app: true,
        normalizationSchema,
        transformResponse: hackFolderCountsResponse(channelType),
        meta: {
          channelType,
          channelId,
          requestKey: camelize(buildFetchFolderCountsRequestKey(channelId)),
        },
      }
    )
  )
}
